import dynamic from 'next/dynamic';

import { useBundle } from '@/lib/hooks';
import {
  isBundle,
  isBundleWithoutVariables,
  isExternal,
  isSimple,
  isVariable,
  PRODUCT_TEMPLATES,
} from '@/lib/utils';

import type { TypeImageWordpress } from '@/components/types';
import type { FunctionComponent } from 'react';
import type { TypeProductDescriptionProps } from '../product-description';
import type { ProductFormLayout } from './types';

const BundleCustomizableProductForm = dynamic(() =>
  import('./BundleCustomizableProductForm').then(
    (mod) => mod.BundleCustomizableProductForm,
  ),
);

const BundleVariableProductForm = dynamic(() =>
  import('./BundleVariableProductForm').then(
    (mod) => mod.BundleVariableProductForm,
  ),
);

const SimpleProductForm = dynamic(() =>
  import('./SimpleProductForm').then((mod) => mod.SimpleProductForm),
);

const VariableProductForm = dynamic(() =>
  import('./VariableProductForm').then((mod) => mod.VariableProductForm),
);

const CustomTagForm = dynamic(() =>
  import('./CustomTagForm').then((mod) => mod.CustomTagForm),
);

interface TypeProductFormsProps
  extends Pick<
      TypeProductDescriptionProps,
      | 'type'
      | 'bundledItems'
      | 'woosbIds'
      | 'template'
      | 'price'
      | 'regularPrice'
      | 'isMirakl'
      | 'externalUrl'
      | 'attributes'
      | 'variations'
    >,
    ProductFormLayout {
  woosbData?: { slug: string; image: TypeImageWordpress };
  exactQuantity?: number[];
  woosbMinQty?: number;
}

/**
 * ProductForms
 */
export const ProductForms: FunctionComponent<TypeProductFormsProps> = ({
  bundledItems,
  woosbIds,
  stock,
  type,
  template,
  price,
  productId,
  regularPrice,
  moreInfo,
  externalUrl,
  isMirakl,
  stockAlert,
  attributes,
  variations,
  isSellerClosed,
  woosbData,
  exactQuantity,
  woosbMinQty,
}: TypeProductFormsProps) => {
  const bundle = useBundle();

  const showSimpleBoxBuy =
    type &&
    (Boolean(isBundle(type) && bundle) ||
      isSimple(type) ||
      isExternal(type) ||
      isBundleWithoutVariables(type, {
        bundledItems,
        woosbIds,
        stock,
      }));

  const isBundleConfigurableLayout =
    type &&
    isBundle(type) &&
    bundle?.optionalProducts &&
    bundle?.variants?.length === 0;

  const isBundleWithVariablesLayout =
    type &&
    isBundle(type) &&
    !bundle &&
    woosbIds &&
    bundledItems &&
    !showSimpleBoxBuy;

  const isSimpleLayout =
    (showSimpleBoxBuy &&
      bundle?.optionalProducts &&
      bundle?.numProducts === bundle?.selectedVariant?.quantity) ||
    (showSimpleBoxBuy && !bundle);

  const isVariableLayout =
    type && isVariable(type) && template !== PRODUCT_TEMPLATES.CUSTOM_TAG;

  const isCustomTagLayout = template === PRODUCT_TEMPLATES.CUSTOM_TAG;

  return (
    <>
      {isBundleConfigurableLayout && (
        <BundleCustomizableProductForm
          productId={productId}
          price={price}
          regularPrice={regularPrice}
          moreInfo={moreInfo}
          stock={stock}
          type={type}
          externalUrl={externalUrl}
          stockAlert={stockAlert}
          isSellerClosed={isSellerClosed}
        />
      )}

      {isBundleWithVariablesLayout && (
        <BundleVariableProductForm
          productId={productId}
          price={price}
          regularPrice={regularPrice}
          moreInfo={moreInfo}
          stock={stock}
          stockAlert={stockAlert}
          isSellerClosed={isSellerClosed}
          bundledItems={bundledItems}
          woosbIds={woosbIds}
          isMirakl={isMirakl}
          attributes={{
            edges: bundledItems
              ?.filter((item) => isVariable(item.type))
              ?.map((item) => item.attributes.edges)
              .flat(),
          }}
          variations={{
            edges: bundledItems
              ?.filter((item) => isVariable(item.type))
              ?.map((item) => item.variations.edges)
              .flat(),
          }}
        />
      )}

      {isSimpleLayout && (
        <SimpleProductForm
          productId={productId}
          price={bundle?.optionalProducts ? bundle?.totalPrice : price}
          regularPrice={
            bundle?.optionalProducts ? bundle?.totalRegularPrice : regularPrice
          }
          moreInfo={moreInfo}
          stock={stock}
          type={type}
          externalUrl={externalUrl}
          stockAlert={stockAlert}
          isSellerClosed={isSellerClosed}
          exactQuantity={exactQuantity}
          woosbData={woosbData}
          woosbMinQty={woosbMinQty}
        />
      )}

      {isVariableLayout && (
        <VariableProductForm
          productId={productId}
          attributes={attributes}
          price={price}
          moreInfo={moreInfo}
          variations={variations}
          stock={stock}
          stockAlert={stockAlert}
          isSellerClosed={isSellerClosed}
          isMirakl={isMirakl}
          exactQuantity={exactQuantity}
          woosbData={woosbData}
          woosbMinQty={woosbMinQty}
        />
      )}

      {isCustomTagLayout && (
        <CustomTagForm
          moreInfo={moreInfo}
          productId={productId}
          variations={variations}
          stock={stock}
          stockAlert={stockAlert}
          isSellerClosed={isSellerClosed}
        />
      )}
    </>
  );
};

ProductForms.displayName = 'ProductForms';
